import { useEffect, useState } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

let fpPromise: Promise<any> | null = null;
let cachedDeviceId: string | null = null;

/**
 * Custom hook to get device fingerprint ID
 * Uses FingerprintJS for device identification
 * Caches the result for performance
 */
export function useDeviceFingerprint() {
    const [deviceId, setDeviceId] = useState<string | null>(cachedDeviceId);
    const [loading, setLoading] = useState(!cachedDeviceId);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        // If already cached, return immediately
        if (cachedDeviceId) {
            setDeviceId(cachedDeviceId);
            setLoading(false);
            return;
        }

        // Initialize FingerprintJS only once
        if (!fpPromise) {
            fpPromise = FingerprintJS.load();
        }

        // Get device fingerprint
        fpPromise
            .then((fp) => fp.get())
            .then((result) => {
                const id = result.visitorId;
                cachedDeviceId = id;
                setDeviceId(id);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Failed to get device fingerprint:', err);
                setError(err);
                setLoading(false);
                // Generate fallback ID based on available browser info
                const fallbackId = generateFallbackDeviceId();
                cachedDeviceId = fallbackId;
                setDeviceId(fallbackId);
            });
    }, []);

    return { deviceId, loading, error };
}

/**
 * Generate a fallback device ID if FingerprintJS fails
 * Uses available browser APIs to create a consistent ID
 */
function generateFallbackDeviceId(): string {
    const components = [
        navigator.userAgent,
        navigator.language,
        new Date().getTimezoneOffset().toString(),
        screen.colorDepth.toString(),
        screen.width + 'x' + screen.height,
        navigator.hardwareConcurrency?.toString() || '0',
    ];

    // Simple hash function
    const str = components.join('|');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }

    return 'fallback_' + Math.abs(hash).toString(16);
}

export default useDeviceFingerprint;
